import React, { useState, useRef, useEffect } from 'react';
import { 
  Camera, 
  CheckCircle, 
  XCircle, 
  Play, 
  Square, 
  RotateCcw,
  Loader2,
  Volume2,
  Maximize2,
  AlertTriangle,
  Wifi
} from 'lucide-react';
import Theta360Viewer from './Theta360Viewer';

/**
 * ThetaCameraTest - Test component for RICOH THETA cameras
 * Allows gestionnaire to verify 360° stream before starting AG
 */
const ThetaCameraTest = ({ 
  stream, 
  isTheta = false, 
  cameraInfo = null,
  onTestComplete,
  compact = false
}) => {
  const [testStatus, setTestStatus] = useState('idle'); // idle, testing, passed, failed
  const [testResults, setTestResults] = useState({
    videoStream: null,
    resolution: null,
    frameRate: null,
    is360: null
  });
  const [showPreview, setShowPreview] = useState(false);
  const videoRef = useRef(null);
  const [videoStats, setVideoStats] = useState(null);

  // Run automatic tests when stream changes
  useEffect(() => {
    if (stream && testStatus === 'idle') {
      runTests();
    }
  }, [stream]);

  // Get video track stats
  const getVideoStats = () => {
    if (!stream) return null;
    
    const videoTrack = stream.getVideoTracks()[0];
    if (!videoTrack) return null;

    const settings = videoTrack.getSettings();
    const capabilities = videoTrack.getCapabilities?.() || {};
    
    return {
      width: settings.width,
      height: settings.height,
      frameRate: settings.frameRate,
      aspectRatio: settings.width && settings.height 
        ? (settings.width / settings.height).toFixed(2) 
        : null,
      deviceId: settings.deviceId,
      label: videoTrack.label,
      enabled: videoTrack.enabled,
      readyState: videoTrack.readyState,
      capabilities
    };
  };

  // Run all tests
  const runTests = async () => {
    if (!stream) {
      setTestStatus('failed');
      setTestResults(prev => ({ ...prev, videoStream: false }));
      return;
    }

    setTestStatus('testing');
    
    // Test 1: Video stream exists and is active
    const videoTrack = stream.getVideoTracks()[0];
    const hasVideo = videoTrack && videoTrack.readyState === 'live';
    
    // Test 2: Get resolution
    const stats = getVideoStats();
    setVideoStats(stats);
    
    // Test 3: Check if resolution suggests 360° (2:1 aspect ratio for equirectangular)
    const aspectRatio = stats?.aspectRatio ? parseFloat(stats.aspectRatio) : 0;
    const is360Format = aspectRatio >= 1.8 && aspectRatio <= 2.2; // Allow some tolerance
    
    // Test 4: Check minimum resolution for 360°
    const hasGoodResolution = stats?.width >= 1920 && stats?.height >= 960;
    
    const results = {
      videoStream: hasVideo,
      resolution: hasGoodResolution,
      frameRate: stats?.frameRate >= 15,
      is360: isTheta && is360Format
    };
    
    setTestResults(results);
    
    // Overall pass/fail
    const allPassed = Object.values(results).every(v => v === true || v === null);
    setTestStatus(allPassed ? 'passed' : (hasVideo ? 'passed' : 'failed'));
    
    onTestComplete?.(allPassed, results);
  };

  // Reset and retest
  const resetTest = () => {
    setTestStatus('idle');
    setTestResults({
      videoStream: null,
      resolution: null,
      frameRate: null,
      is360: null
    });
    setVideoStats(null);
    setShowPreview(false);
    
    // Run tests again after reset
    setTimeout(() => {
      if (stream) runTests();
    }, 500);
  };

  // Render test item
  const TestItem = ({ label, status, detail }) => (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-gray-700">{label}</span>
      <div className="flex items-center gap-2">
        {detail && <span className="text-xs text-gray-500">{detail}</span>}
        {status === null && <span className="w-4 h-4 bg-gray-200 rounded-full"></span>}
        {status === true && <CheckCircle className="text-green-500" size={16} />}
        {status === false && <XCircle className="text-red-500" size={16} />}
      </div>
    </div>
  );

  // Compact mode - just show status badge
  if (compact) {
    return (
      <div className="flex items-center gap-2">
        {testStatus === 'testing' && (
          <span className="flex items-center gap-1 text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
            <Loader2 size={12} className="animate-spin" />
            Test en cours...
          </span>
        )}
        {testStatus === 'passed' && (
          <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
            <CheckCircle size={12} />
            Caméra OK
          </span>
        )}
        {testStatus === 'failed' && (
          <span className="flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-1 rounded-full">
            <XCircle size={12} />
            Erreur
          </span>
        )}
        {stream && (
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="text-xs text-[#00D3A8] hover:underline"
          >
            {showPreview ? 'Masquer' : 'Aperçu'}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid="theta-camera-test">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
        <div className="flex items-center gap-2">
          <Camera className="text-[#00D3A8]" size={18} />
          <span className="font-medium text-gray-900 text-sm">Test de la caméra</span>
          {cameraInfo?.model && (
            <span className="text-xs bg-[#00D3A8]/10 text-[#00D3A8] px-2 py-0.5 rounded">
              {cameraInfo.model}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {testStatus === 'passed' && (
            <span className="flex items-center gap-1 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
              <CheckCircle size={12} />
              Prêt
            </span>
          )}
          {testStatus === 'failed' && (
            <span className="flex items-center gap-1 text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full">
              <AlertTriangle size={12} />
              Problème détecté
            </span>
          )}
          <button
            onClick={resetTest}
            className="p-1.5 text-gray-500 hover:text-[#00D3A8] hover:bg-gray-100 rounded"
            title="Relancer le test"
          >
            <RotateCcw size={16} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* No stream */}
        {!stream && (
          <div className="text-center py-6 bg-gray-50 rounded-lg">
            <Camera size={32} className="mx-auto text-gray-400 mb-2" />
            <p className="text-sm text-gray-600">Sélectionnez une caméra pour lancer le test</p>
          </div>
        )}

        {/* Testing in progress */}
        {stream && testStatus === 'testing' && (
          <div className="text-center py-6">
            <Loader2 size={32} className="mx-auto text-[#00D3A8] animate-spin mb-2" />
            <p className="text-sm text-gray-600">Test en cours...</p>
          </div>
        )}

        {/* Test results */}
        {stream && (testStatus === 'passed' || testStatus === 'failed') && (
          <div className="space-y-4">
            {/* Test items */}
            <div className="space-y-1 divide-y divide-gray-100">
              <TestItem 
                label="Flux vidéo actif" 
                status={testResults.videoStream}
                detail={videoStats?.readyState}
              />
              <TestItem 
                label="Résolution" 
                status={testResults.resolution}
                detail={videoStats ? `${videoStats.width}×${videoStats.height}` : null}
              />
              <TestItem 
                label="Fréquence d'images" 
                status={testResults.frameRate}
                detail={videoStats?.frameRate ? `${Math.round(videoStats.frameRate)} fps` : null}
              />
              {isTheta && (
                <TestItem 
                  label="Format 360° (2:1)" 
                  status={testResults.is360}
                  detail={videoStats?.aspectRatio ? `Ratio ${videoStats.aspectRatio}` : null}
                />
              )}
            </div>

            {/* Preview toggle */}
            <div className="flex items-center justify-between pt-2">
              <button
                onClick={() => setShowPreview(!showPreview)}
                className="flex items-center gap-2 text-sm text-[#00D3A8] hover:text-[#00B894] font-medium"
              >
                {showPreview ? <Square size={16} /> : <Play size={16} />}
                {showPreview ? 'Masquer l\'aperçu' : 'Voir l\'aperçu'}
              </button>
              
              {testStatus === 'passed' && (
                <span className="flex items-center gap-1 text-xs text-green-600">
                  <Wifi size={14} />
                  Caméra prête pour l'AG
                </span>
              )}
            </div>

            {/* Preview window */}
            {showPreview && stream && (
              <div className="mt-3 rounded-lg overflow-hidden bg-gray-900" style={{ height: '200px' }}>
                {isTheta ? (
                  <Theta360Viewer 
                    stream={stream}
                    isActive={true}
                    className="w-full h-full"
                    showControls={true}
                  />
                ) : (
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    className="w-full h-full object-cover"
                    srcObject={stream}
                    onLoadedMetadata={() => {
                      if (videoRef.current) {
                        videoRef.current.srcObject = stream;
                      }
                    }}
                  />
                )}
              </div>
            )}

            {/* Troubleshooting tips for failed tests */}
            {testStatus === 'failed' && (
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <h5 className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-1">
                  <AlertTriangle size={14} />
                  Conseils de dépannage
                </h5>
                <ul className="text-xs text-amber-700 space-y-1">
                  {!testResults.videoStream && (
                    <li>• Vérifiez que la caméra est bien connectée via USB</li>
                  )}
                  {!testResults.resolution && (
                    <li>• Activez le mode "Live Streaming" sur votre THETA</li>
                  )}
                  {isTheta && !testResults.is360 && (
                    <li>• Vérifiez que le mode UVC 4K est activé dans les paramètres THETA</li>
                  )}
                  <li>• Déconnectez puis reconnectez la caméra</li>
                  <li>• Redémarrez la caméra si nécessaire</li>
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ThetaCameraTest;
